import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { menu } from '../../exports/menu';
import { HttpService } from '../../services/http.service';
import { ToolsService } from '../../services/tools.service';
import {
  GlobalSearchDialogService,
  SearchResultData,
} from './global-search-dialog.service';

export interface PageResult {
  path: string;
  title: string;
  icon: string;
}

/** Map type → { icon, sectionLabel } pour l’affichage des résultats données. */
const DATA_TYPE_META: Record<
  string,
  { icon: string; sectionLabel: string }
> = {
  actionnaire: { icon: 'group', sectionLabel: 'Actionnaire' },
  administre: { icon: 'shield', sectionLabel: 'Compte administré' },
  banque: { icon: 'account_balance', sectionLabel: 'Guichet' },
  civilite: { icon: 'male', sectionLabel: 'Civilité' },
  groupe: { icon: 'groups', sectionLabel: 'Groupe' },
  pays: { icon: 'public', sectionLabel: 'Pays' },
  typebanque: { icon: 'assured_workload', sectionLabel: 'Enseigne bancaire' },
  mouvement: { icon: 'sync_alt', sectionLabel: 'Mouvement' },
};

@Component({
  selector: 'app-global-search-dialog',
  templateUrl: './global-search-dialog.component.html',
  styleUrls: ['./global-search-dialog.component.scss'],
  standalone: false,
})
export class GlobalSearchDialogComponent implements OnDestroy, AfterViewInit {
  @ViewChild('input', { static: false }) inputRef: ElementRef<HTMLInputElement>;

  query = '';
  pageResults: PageResult[] = [];
  dataResults: SearchResultData[] = [];
  loading = false;
  private search$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  readonly menu = menu;
  readonly dataTypeMeta = DATA_TYPE_META;

  constructor(
    public dialogRef: MatDialogRef<GlobalSearchDialogComponent>,
    private _http: HttpService,
    private _router: Router,
    public _tools: ToolsService,
    private _globalSearch: GlobalSearchDialogService
  ) {
    this.search$
      .pipe(
        debounceTime(280),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((q) => this.runSearch(q));
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.inputRef?.nativeElement?.focus(), 50);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.dialogRef.close();
    }
  }

  onInput(): void {
    this.search$.next(this.query.trim());
  }

  private runSearch(q: string): void {
    this.searchPages(q);
    if (q.length >= 2) {
      this.searchData(q);
    } else {
      this.dataResults = [];
    }
  }

  private searchPages(q: string): void {
    if (!q) {
      this.pageResults = [];
      return;
    }
    const lower = q.toLowerCase();
    const out: PageResult[] = [];
    for (const m of this.menu) {
      for (const s of m.sublinks) {
        const text = `${s.title} ${s.path} ${m.title}`.toLowerCase();
        if (text.includes(lower)) {
          out.push({ path: s.path, title: s.title, icon: s.icon });
        }
      }
    }
    this.pageResults = out;
  }

  private searchData(q: string): void {
    this.loading = true;
    this._http.GET(
      '/api/search',
      { q },
      (res: { isOk: boolean; obj?: { results?: SearchResultData[] } }) => {
        this.loading = false;
        this.dataResults = res?.obj?.results ?? [];
      },
      (err) => {
        this.loading = false;
        this.dataResults = [];
        this._tools.showHttpError(err);
      }
    );
  }

  selectPage(p: PageResult): void {
    this.dialogRef.close();
    const base = this._router.url.split('/').filter(Boolean)[0] ?? '';
    this._router.navigate([base, p.path]);
  }

  selectData(r: SearchResultData): void {
    this.dialogRef.close();
    this._globalSearch.openEntityDialog(r);
  }

  getDataSectionLabel(type: string): string {
    return this.dataTypeMeta[type]?.sectionLabel ?? type;
  }

  getDataIcon(type: string): string {
    return this.dataTypeMeta[type]?.icon ?? 'label';
  }
}
